// src/app/useAppRouteGuard.ts

import { useEffect } from 'react';
import { useNavigate, useRouterState } from '@tanstack/react-router';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { RouteAccess } from './routeAccess';

/**
 * App-wide permission redirects, mounted once in AppShell (mirrors the
 * AccountLayout guard pattern). Skipped while the account-closed takeover is
 * active. Returns isRedirecting so the shell can render a loader instead of
 * the outlet for the frame before the redirect commits — no content flash,
 * no doomed queries fired.
 */
export function useAppRouteGuard(isAccountClosed: boolean): { isRedirecting: boolean } {
  const navigate = useNavigate();
  const routerState = useRouterState();
  const { authData, isUploadOnly, canViewMedia } = useAuth();
  const currentPath = routerState.location.pathname;

  const target =
    isAccountClosed || !authData
      ? null
      : RouteAccess.getRedirect(currentPath, {
          isUploadOnly,
          canIngest: authData.cpuser.permissions.ingest,
          canViewMedia,
        });

  useEffect(() => {
    if (target) {
      navigate({ to: target, replace: true });
    }
  }, [target, navigate]);

  return { isRedirecting: target != null };
}
